import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import * as actions from '../../../../store/index';

import QualitySummary from './Quality/QualitySummary';
import DensitySummary from './Density/DensitySummary';
import DiagnosticsSummary from './Diagnostics/DiagnosticsSummary';
import RiskSummary from './Risk/RiskSummary';
import Limitations from './Limitations/Limitations';

function Summary() {
  const dispatch = useDispatch();
  // Store state
  const results = useSelector((state) => state.results);
  const zoomedProjection = useSelector((state) => state.gui?.zoomedProjection);
  const {
    applied_quality,
    applied_density,
    shown_quality,
    shown_density,
    shown_opacities,
    applied_opacities,
    shown_microcalc,
    applied_microcalc,
  } = useSelector((state) => state.results);
  const { premium_features } = useSelector((state) => state.global_settings);

  const { diagnostics_microcalc_module, diagnostics_opacities_module, density_module, quality_module, risk_module } =
    premium_features;

  const shownQuality = shown_quality || applied_quality;
  const shownDensity = shown_density || applied_density;
  const shownOpacities = shown_opacities || applied_opacities;
  const today = new Date();
  const birthDate = new Date(results?.birth);
  const currentRole = localStorage.getItem('role');

  //Loged user role & premium modules
  let diagnosticsShown;
  try {
    diagnosticsShown =
      (diagnostics_microcalc_module || diagnostics_opacities_module) &&
      localStorage.getItem('diagnostics') === 'true';
  } catch {
    diagnosticsShown = false;
  }

  const allPremiumModules = diagnosticsShown && density_module && risk_module;
  const noPremiumModules = !diagnosticsShown && !density_module && !risk_module;

  useEffect(() => {
    if (!shown_quality) dispatch(actions.setShownQuality(applied_quality));
    if (!shown_density) dispatch(actions.setShownDensity(applied_density));
    if (!shown_opacities) dispatch(actions.setShownOpacities(applied_opacities));
    if (!shown_microcalc) dispatch(actions.setShownMicrocalc(applied_microcalc));
  }, [results]);

  // Handlers
  const getAge = () => {
    let age = today.getFullYear() - birthDate.getFullYear();
    let m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  function computeGridAreas(mods) {
    const areas = {};

    const { quality, density, diagnostics, risk, limitations } = mods;

    if (!quality) {
      const enabled = ['density', 'diagnostics', 'risk', 'limitations'].filter((name) => mods[name]);
      enabled.forEach((name, index) => {
        const start = 1 + Math.floor(index * 12 / enabled.length);
        const end = 1 + Math.floor((index + 1) * 12 / enabled.length);
        areas[name] = `${start} / 1 / ${end} / 13`;
      });
      return areas;
    }

    // ✅ CASE 1: all premium modules present (diagnostics + density + risk)
    if (allPremiumModules) {
      areas.quality = '1 / 1 / 4 / 8'; // top-left half
      areas.density = '1 / 8 / 4 / 13'; // top-right half
      areas.diagnostics = limitations ? '4 / 1 / 8 / 13' : '4 / 1 / 9 / 13'; // big block
      areas.risk = limitations ? '8 / 1 / 11 / 13' : '9 / 1 / 13 / 13';
      if (limitations) areas.limitations = '11 / 1 / 13 / 13';
      return areas;
    }

    // ✅ CASE 2: only quality present (no premium modules)
    if (quality && noPremiumModules) {
      areas.quality = limitations ? '1 / 1 / 7 / 13' : '1 / 1 / 13 / 13';
      if (limitations) areas.limitations = '7 / 1 / 13 / 13';
      return areas;
    }

    // ✅ CASE 3: quality + density + diagnostics (no risk)
    if (quality && density && diagnostics && !risk) {
      areas.quality = '1 / 1 / 5 / 8'; // top-left half
      areas.density = '1 / 8 / 5/ 13'; // top-right half
      areas.diagnostics = limitations ? '5 / 1 / 10 / 13' : '5 / 1 / 13 / 13'; // big block
      if (limitations) areas.limitations = '10 / 1 / 13 / 13';
      return areas;
    }

    // ✅ CASE 4: quality + diagnostics (no density, no risk)
    if (quality && diagnostics && !density && !risk) {
      areas.quality = '1 / 1 / 4 / 13'; // top section
      areas.diagnostics = limitations ? '4 / 1 / 10 / 13' : '4 / 1 / 13 / 13'; // big block
      if (limitations) areas.limitations = '10 / 1 / 13 / 13';
      return areas;
    }

    // ✅ CASE 5: quality + density (no diagnostics, no risk)
    if (quality && density && !diagnostics && !risk) {
      areas.quality = limitations ? '1 / 1 / 6 / 13' : '1 / 1 / 7 / 13';
      areas.density = limitations ? '6 / 1 / 10 / 13' : '7 / 1 / 13 / 13';
      if (limitations) areas.limitations = '10 / 1 / 13 / 13';
      return areas;
    }

    // ✅ CASE 6: quality + risk (no density, no diagnostics)
    if (quality && risk && !density && !diagnostics) {
      areas.quality = limitations ? '1 / 1 / 6 / 13' : '1 / 1 / 7 / 13';
      areas.risk = limitations ? '6 / 1 / 10 / 13' : '7 / 1 / 13 / 13';
      if (limitations) areas.limitations = '10 / 1 / 13 / 13';
      return areas;
    }

    // ✅ CASE 7: quality + density + risk (no diagnostics)
    if (quality && density && risk && !diagnostics) {
      areas.quality = limitations ? '1 / 1 / 5 / 8' : '1 / 1 / 5 / 13'; // top-left half
      areas.density = limitations ? '1 / 8 / 5 / 13' : '5/ 1 / 8 / 13'; // top-right half if no limitations or 2nd column
      areas.risk = limitations ? '5 / 1 / 10 / 13' : '8 / 1 / 13 / 13'; // if no limitations or 3rd column
      if (limitations) areas.limitations = '10 / 1 / 13 / 13';
      return areas;
    }

    // ✅ CASE 8: quality + diagnostics + risk (no density)
    if (quality && diagnostics && risk && !density) {
      areas.quality = '1 / 1 / 4 / 13'; // top section
      areas.diagnostics = limitations ? '4 / 1 / 8 / 13' : '4 / 1 / 9 / 13'; // big block
      areas.risk = limitations ? '8 / 1 / 11 / 13' : '9 / 1 / 13 / 13';
      if (limitations) areas.limitations = '11 / 1 / 13 / 13';
      return areas;
    }

    // ✅ CASE 9: Fallback - stack vertically for any other combination
    let currentRow = 1;

    if (quality) {
      areas.quality = `${currentRow} / 1 / ${currentRow + 3} / 13`;
      currentRow += 3;
    }

    if (density) {
      areas.density = `${currentRow} / 1 / ${currentRow + 3} / 13`;
      currentRow += 3;
    }

    if (diagnostics) {
      areas.diagnostics = `${currentRow} / 1 / ${currentRow + 6} / 13`; // bigger block
      currentRow += 6;
    }

    if (risk) {
      areas.risk = `${currentRow} / 1 / ${limitations ? currentRow + 4 : currentRow + 2} / 13`;
      currentRow += limitations ? 4 : 2;
    }

    if (limitations) {
      areas.limitations = `${currentRow} / 1 / 13 / 13`; // fill the rest
    }

    return areas;
  }

  const modules = {
    quality: quality_module,
    density: density_module,
    diagnostics: diagnosticsShown,
    risk: risk_module,
    limitations: currentRole === 'technician',
  };

  const areas = computeGridAreas(modules);

  const isLoading = !results || !results.evaluations;

  const LoadingSkeleton = () => (
    <Card style={{ height: '100%', padding: '16px' }}>
      <Skeleton variant='text' width='60%' height={32} style={{ marginBottom: '16px' }} />
      <Skeleton variant='rect' width='100%' height={120} style={{ marginBottom: '16px' }} />
      <Skeleton variant='text' width='80%' height={20} />
    </Card>
  );

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(12, 1fr)',
        gridTemplateRows: 'repeat(12, 1fr)',
        gap: '6px',
        height: '100%',
        width: '100%',
      }}>
      {isLoading ? (
        <>
          {/* Show all skeletons when loading */}
          <div style={{ gridArea: '1 / 1 / 4 / 8' }}>
            <LoadingSkeleton />
          </div>
          <div style={{ gridArea: '1 / 8 / 4 / 13' }}>
            <LoadingSkeleton />
          </div>
          <div style={{ gridArea: '4 / 1 / 9 / 13' }}>
            <LoadingSkeleton />
          </div>
          <div style={{ gridArea: '9 / 1 / 13 / 13' }}>
            <LoadingSkeleton />
          </div>
        </>
      ) : (
        <>
          {/* Row 1: Quality (left) */}
          {modules.quality && (
            <div style={{ gridArea: areas.quality }}>
              <QualitySummary
                results={results?.evaluations?.quality?.[shownQuality]}
                diagnosticsShown={diagnosticsShown}
              />
            </div>
          )}

          {/* Row 1: Density (right) */}
          {modules.density && (
            <div style={{ gridArea: areas.density }}>
              <DensitySummary
                results={results?.evaluations?.density?.[shownDensity]}
                zoomedProjection={zoomedProjection}
              />
            </div>
          )}

          {/* Diagnostics */}
          {modules.diagnostics && (
            <div style={{ gridArea: areas.diagnostics }}>
              <DiagnosticsSummary />
            </div>
          )}

          {/* Risk */}
          {modules.risk && (
            <div style={{ gridArea: areas.risk }}>
              <RiskSummary age={getAge()} results={results} />
            </div>
          )}

          {/* Limitations summary */}
          {modules.limitations && (
            <div
              style={{
                gridArea: areas.limitations,
              }}>
              <Limitations />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Summary;
